/**
 * Reading a carrier's rate card.
 *
 * WHY THIS LIVES HERE AND NOT IN A SCREEN
 * It was written inside `products.ts`, which imports the DOM, so it could not be
 * imported by a test and never had one. That matters more than it sounds: this
 * function is the single gate between a carrier's printed rate book and every
 * premium the quoter will ever show a family. It is the most consequential
 * parser in the system and it was the only one nobody could test.
 *
 * WHAT IT REFUSES TO DO
 * Guess. A cell it cannot read is an error naming the row and the column, never
 * a silent skip and never a default. The quoter's whole promise is that a
 * premium came off a real card; a parser that quietly drops or invents a cell
 * breaks that promise somewhere nobody will look until a family is told a price
 * the carrier will not honour.
 */

/** Only the part of a product this parser needs. */
export interface RateCardProduct {
  /** `per_thousand` prices per $1,000 of cover; `table` prices per face amount. */
  rateMode: string;
}

export interface GridChoice {
  sex: 'male' | 'female';
  tobacco: boolean;
  rateClass?: string;
}

export type RateRow = Record<string, unknown>;

/** Nobody is issued life insurance at 0, and nobody is issued it at 130. */
const AGE_MIN = 0;
const AGE_MAX = 120;

/**
 * Split one line into cells, whichever way the card was pasted.
 *
 * THREE SHAPES, AND TWO OF THEM USED TO FAIL
 * A spreadsheet gives commas or tabs. A PDF — which is what a producer guide
 * is — usually gives neither: copying a printed table lands as columns held
 * apart by runs of spaces. That paste was rejected outright with a message
 * about a missing "sex" column, which is not a clue, it is a dead end, and the
 * reasonable conclusion is that the feature is broken.
 *
 * Worse, a comma-separated card whose headings are dollar amounts —
 * `Age,$5,000,$10,000`, which is how every final expense card in the world is
 * printed — split on the thousands separators inside its own headings and
 * produced five columns out of three. Same dead-end error, from a completely
 * ordinary card.
 */
export function splitCells(line: string): string[] {
  const hasTab = line.includes('\t');
  const hasComma = line.includes(',');

  if (hasTab) return line.split('\t').map((c) => c.trim());

  if (hasComma) {
    // Quoted fields first, so `"$10,000"` survives the comma inside it. Then a
    // bare `$5,000` — a currency amount whose comma is a thousands separator
    // rather than a cell boundary, which is the case that broke every real card.
    const cells: string[] = [];
    let cur = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i]!;
      if (ch === '"') { quoted = !quoted; continue; }
      if (ch === ',' && !quoted) {
        /*
         * A comma inside a currency amount is a thousands separator, not a cell
         * boundary — but ONLY when the amount carries a currency symbol.
         *
         * The first version of this rule tried to infer it from shape alone:
         * digits, comma, three digits. That is genuinely ambiguous, and it got
         * the ordinary case wrong. `58,120.00` is age 58 at $120.00 a month,
         * which is what most of a final expense card looks like, and it was
         * being read as the single number fifty-eight thousand one hundred and
         * twenty. It threw rather than loading garbage, but it threw on cards
         * that were perfectly fine.
         *
         * So: `$5,000` merges, because the `$` says it is one amount.
         * `58,120.00` splits, because nothing says otherwise. An unquoted
         * `5,000` with no symbol stays ambiguous and is left to split — the
         * grid check then refuses it with a message naming the headings, which
         * is the right outcome for input that genuinely could be either.
         */
        const before = cur.trimEnd();
        const after = line.slice(i + 1);
        const currencyRun = /(?:^|[\s(])[$£€]\s?[\d,]*\d$/.test(before);
        if (currencyRun && /^\d{3}(?![\d])/.test(after)) {
          cur += ch;
          continue;
        }
        cells.push(cur.trim());
        cur = '';
        continue;
      }
      cur += ch;
    }
    cells.push(cur.trim());
    return cells;
  }

  // Neither: columns held apart by whitespace, the way a PDF pastes.
  return line.trim().split(/\s{2,}|\s+/).map((c) => c.trim()).filter(Boolean);
}

/**
 * The ages one row of a card covers.
 *
 * A card that prints `50-54` means every age from 50 to 54, and expanding it is
 * a faithful reading of what the carrier published, not a guess about it.
 *
 * It used to strip the punctuation and read `50-54` as age **5054**. No error,
 * no warning: the card loaded, the agency believed it had rates, and the quoter
 * then found nothing at all for a real 52-year-old. The failure surfaced as
 * "this product has no rates", weeks later, with nothing pointing at why.
 */
export function agesIn(raw: string): number[] {
  const cell = raw.trim();
  if (!cell) return [];

  const band = /^(\d{1,3})\s*(?:-|–|—|\bto\b|\bthru\b|\bthrough\b)\s*(\d{1,3})$/i.exec(cell);
  if (band) {
    const lo = Number(band[1]);
    const hi = Number(band[2]);
    if (lo > hi) throw new Error(`"${cell}" runs backwards.`);
    if (hi - lo > 60) throw new Error(`"${cell}" covers more than sixty ages, so it is not an age band.`);
    const out: number[] = [];
    for (let a = lo; a <= hi; a += 1) out.push(a);
    return out;
  }

  // "85+", "85 and over", "50 & under" — an open end. One age, because the
  // card gives one rate and inventing the others would be inventing rates.
  const open = /^(\d{1,3})\s*(?:\+|and over|and above|and up|&\s*over|and under|&\s*under|or younger|or older)$/i
    .exec(cell);
  const single = open ?? /^(\d{1,3})(?:\.0+)?$/.exec(cell);
  if (!single) return [];
  return [Number(single[1])];
}

const clean = (raw: string): string => raw.replace(/[$,\s]/g, '');

/** A number of dollars, or null when the cell is not one. */
function money(raw: string): number | null {
  const s = clean(raw);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Cells that mean "not offered at this age", which is real and not a typo. */
const BLANK = new Set(['', '-', '–', '—', 'n/a', 'na', 'x', '--']);

/**
 * Read a pasted rate card into rows the API will accept.
 *
 * Two shapes, detected rather than chosen. A GRID has ages down the left and
 * face amounts (or term lengths) across the top — which is how every carrier
 * actually prints one, and requiring anything else meant retyping thousands of
 * cells by hand. LONG format has one row per rate and words in its headings.
 */
export function parseCard(
  text: string,
  product: RateCardProduct,
  grid: GridChoice,
): RateRow[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) throw new Error('Paste the card in, with its column names on the first line.');

  const head = splitCells(lines[0]!).map((h) => h.toLowerCase());

  const headerNumbers = head.slice(1).map((h) => Number(clean(h)));
  const isGrid = head.length >= 2
    && /^(age|issue age|ages|attained age|age band)$/.test(head[0]!.trim())
    && headerNumbers.length > 0
    && headerNumbers.every((n) => Number.isFinite(n) && n > 0);

  if (isGrid) return parseGrid(lines, headerNumbers, product, grid);

  const col = (...names: string[]): number => {
    for (const n of names) {
      const i = head.indexOf(n);
      if (i !== -1) return i;
    }
    return -1;
  };
  const iSex = col('sex', 'gender');
  const iTob = col('tobacco', 'smoker', 'tob');
  const iAge = col('age', 'issue age', 'ages');
  const iRate = col('monthly', 'premium', 'rate', 'monthly premium');
  const iFace = col('face', 'face amount', 'coverage', 'benefit');
  const iTerm = col('term', 'term years');
  const iClass = col('class', 'rate class', 'health class');

  /*
   * The error a person actually gets when a grid was not recognised.
   *
   * Before, a normal card that failed the grid test fell through to here and
   * was told it needed a "sex" column — about a card that has ages down the
   * side and dollar amounts across the top and never had a sex column in its
   * life. The message named a real requirement of a shape the person was not
   * using, so the only possible conclusion was that the tool was broken.
   */
  if (iSex === -1 && iAge !== -1 && iRate === -1) {
    throw new Error(
      `This looks like a grid — ages down the side, amounts across the top — but the headings `
      + `after "${head[0]}" are not all amounts: ${head.slice(1).join(', ')}. Check the first `
      + 'line is the face amounts (or term lengths) and nothing else.',
    );
  }

  for (const [label, idx] of [['sex', iSex], ['tobacco', iTob], ['age', iAge]] as const) {
    if (idx === -1) throw new Error(`The card needs a "${label}" column. Found: ${head.join(', ')}.`);
  }
  if (iRate === -1) {
    throw new Error(`The card needs a "${product.rateMode === 'per_thousand' ? 'rate' : 'monthly'}" column. Found: ${head.join(', ')}.`);
  }
  if (product.rateMode === 'table' && iFace === -1) {
    throw new Error('This product prices per face amount, so the card needs a "face" column.');
  }

  const truthy = (v: string): boolean => ['yes', 'y', 'true', '1', 't', 'smoker'].includes(v.toLowerCase());

  const out: RateRow[] = [];
  for (const [i, line] of lines.slice(1).entries()) {
    const cells = splitCells(line);
    const at = (idx: number): string => (idx === -1 ? '' : cells[idx] ?? '');
    const where = `Row ${i + 2}`;

    const sexRaw = at(iSex).toLowerCase();
    const sex = sexRaw.startsWith('f') ? 'female' : sexRaw.startsWith('m') ? 'male' : null;
    if (!sex) throw new Error(`${where}: "${at(iSex)}" is not male or female.`);

    const ages = agesIn(at(iAge));
    if (!ages.length) throw new Error(`${where}: "${at(iAge)}" is not an age.`);

    const rate = money(at(iRate));
    if (rate === null || rate < 0) throw new Error(`${where}: "${at(iRate)}" is not a rate.`);

    const face = iFace === -1 ? null : money(at(iFace));
    if (product.rateMode === 'table' && face === null) {
      throw new Error(`${where}: "${at(iFace)}" is not a face amount.`);
    }
    const term = iTerm === -1 || !at(iTerm) ? null : money(at(iTerm));

    for (const age of ages) {
      assertAge(age, where, at(iAge));
      out.push({
        sex,
        tobacco: truthy(at(iTob)),
        age,
        // Dollars on the card, cents in the database. Converted once, here.
        rateCents: Math.round(rate * 100),
        ...(face !== null ? { faceCents: Math.round(face * 100) } : {}),
        ...(term !== null ? { termYears: Math.trunc(term) } : {}),
        ...(iClass !== -1 && at(iClass) ? { rateClass: at(iClass) } : {}),
      });
    }
  }
  return out;
}

function assertAge(age: number, where: string, raw: string): void {
  if (!Number.isInteger(age) || age < AGE_MIN || age > AGE_MAX) {
    throw new Error(
      `${where}: "${raw}" reads as age ${age}, which nobody is. If that is an age band, `
      + 'write it as 50-54.',
    );
  }
}

export function parseGrid(
  lines: string[],
  columns: number[],
  product: RateCardProduct,
  grid: GridChoice,
): RateRow[] {
  const perThousand = product.rateMode === 'per_thousand';
  const out: RateRow[] = [];

  for (const [i, line] of lines.slice(1).entries()) {
    const cells = splitCells(line);
    const where = `Row ${i + 2}`;
    const ageRaw = cells[0] ?? '';

    const ages = agesIn(ageRaw);
    if (!ages.length) {
      /*
       * A number where an age should be, and not a plausible one. This is the
       * silent failure this parser used to have: `50-54` had its punctuation
       * stripped and loaded as age **5054**. No error — the card "loaded", the
       * agency believed it had rates, and the quoter then found nothing for a
       * real 52-year-old. It surfaced weeks later as "this product has no
       * rates", with nothing pointing at why.
       */
      if (/^[\d\s.,-]+$/.test(ageRaw) && /\d/.test(ageRaw)) {
        throw new Error(
          `${where}: "${ageRaw}" is not an age. If it is an age band, write it as 50-54.`,
        );
      }
      /*
       * Otherwise a footer, and there is one on nearly every card: "Rates
       * effective 04/2026", "Includes $36 annual policy fee". Skipped when it
       * carries no premiums, because refusing a whole paste over a line of
       * small print sends a person back into a PDF to delete a sentence.
       *
       * Skipped only when nothing in the row reads as money. A line with
       * premiums on it is not small print, and treating it as such would
       * silently drop a row of real rates — which is the same class of failure
       * as the one above, in the other direction.
       */
      const rest = cells.slice(1)
        .filter((c) => !BLANK.has(c.toLowerCase()))
        .filter((c) => money(c) !== null);
      if (rest.length === 0) continue;
      throw new Error(`${where}: "${ageRaw}" is not an age, but the row has rates in it.`);
    }
    for (const age of ages) assertAge(age, where, ageRaw);

    for (const [c, column] of columns.entries()) {
      const raw = cells[c + 1];
      if (raw === undefined || BLANK.has(raw.toLowerCase())) continue;
      const value = money(raw);
      if (value === null || value < 0) {
        throw new Error(`${where}, column "${column}": "${raw}" is not a premium.`);
      }
      for (const age of ages) {
        out.push({
          sex: grid.sex,
          tobacco: grid.tobacco,
          age,
          rateCents: Math.round(value * 100),
          ...(perThousand
            ? { termYears: Math.trunc(column) }
            : { faceCents: Math.round(column * 100) }),
          ...(grid.rateClass ? { rateClass: grid.rateClass } : {}),
        });
      }
    }
  }

  if (!out.length) throw new Error('That grid has no premiums in it.');
  return out;
}
